import { ICellRendererParams } from '@ag-grid-community/core';

import { Shipment } from '../../../../order/models/shipment';
import { UserDatacenterView } from '../../../models/datacenter-aggrid-view';
import { TranslationService } from '../../../services/translation.service';
import { MultiLineValueTypes } from '../../cell-renderers/multi-line-value-types';
import {
	CommonColDef,
	LinkColDef,
	LinkMultiValueColDef,
	MultiLineColDef,
	NumericColDef,
	DateColDef
} from '../../column-defs';
import { ColumnType } from '../../column-type';
import { DatacenterNumberFilterComponent } from '../../filter-type/datacenter-number-filter.component';
import { DatacenterTextFilterComponent } from '../../filter-type/datacenter-text-filter.component';
import { resolveStringCellValue } from '../../../utils/resolved-concatenated-value';
import { DatacenterDropdownFilterComponent } from '../../filter-type/datacenter-dropdown-filter.component';
import { DatacenterDateFilterComponent } from '../../filter-type/datacenter-date-filter.component';
import { DatacenterDimensionFilterComponent } from '../../filter-type/datacenter-dimension-filter.component';
import { formatDimension } from '../../../utils/number.format';

const linkToOrderCardCellRendererParams = {
	routerLink(_value: string, index: number, params: ICellRendererParams<Shipment, unknown>) {
		if (!params.data) {
			return undefined;
		}
		const shipment = params.data;
		const supplierOrderNumber = resolveStringCellValue(shipment.supplierOrderAndItemConcat)[index];
		return {
			commands: [
				'/order-tracking',
				'order',
				'card',
				supplierOrderNumber.split('/')[0],
				supplierOrderNumber.split('/')[1]
			],
		};
	}
};

const linkToBatchCardFromMultilineCellRendererParams = {
	routerLink(_value: string, index: number, params: ICellRendererParams<Shipment, unknown>) {
		if (!params.data) {
			return undefined;
		}
		const shipment = params.data;
		const batchId = resolveStringCellValue(shipment.supplierBatchReferenceConcat)[index];
		return {
			commands: [
				'/order-tracking',
				'batch',
				'card',
				batchId
			]
		};
	}
};

const linkToShipmentCardCellRendererParams = {
	routerLink(params: ICellRendererParams<Shipment, unknown>) {
		const shipment = params.data;
		return shipment?.supplierShipmentNumber
			? { commands: [
				'/order-tracking',
				'shipment',
				'card',
				shipment.supplierShipmentNumber,
			] }
			: undefined;
	}
};
export class ShipmentDatacenterColumnDefinition {
	readonly SHIPMENT_REQUIRED_FIELDS = ['supplierOrderAndItemConcat'];

	readonly SHIPMENT_DATA_CENTER_DEFAULT_CONFIGURATION: Map<string, CommonColDef<Shipment>
	| CommonColDef<Shipment, string>>;

	// eslint-disable-next-line max-lines-per-function
	constructor(private readonly translationService: TranslationService) {
		this.SHIPMENT_DATA_CENTER_DEFAULT_CONFIGURATION = new Map<string, CommonColDef<Shipment>
		| CommonColDef<Shipment, string>>([
			[
				'batchReference',
				new LinkMultiValueColDef<Shipment>({
					autoHeight: true,
					headerName: this.translationService.getTR('datacenter-shipment.batch'),
					valueGetter: params => {
						const shipment = params.data;
						return shipment ? shipment.batchReferenceConcat : '';
					},
					fields: [
						'batchReferenceConcat',
						'supplierBatchReferenceConcat'
					],
					field: 'batchReferenceConcat',
					colId: 'batchReference',
					minWidth: 150,
					cellRendererParams: linkToBatchCardFromMultilineCellRendererParams,
					filter: DatacenterTextFilterComponent,
					filterParams: { filterType: 'SHIPMENT_MULTI_VALUE_TEXT_FILTER' },
					columnType: ColumnType.TEXT_COLUMN,
					suppressHeaderMenuButton: false,
					sortable: true
				})
			],
			[
				'steelgrade',
				new MultiLineColDef<Shipment>({
					headerName: this.translationService.getTR('datacenter-shipment.grade'),
					field: 'steelgradeDescriptionConcat',
					colId: 'steelgrade',
					filter: DatacenterTextFilterComponent,
					filterParams: { filterType: 'SHIPMENT_MULTI_VALUE_TEXT_FILTER' },
					valueType: MultiLineValueTypes.TEXT,
					sortable: true
				})
			],
			[
				'customerArticleReference',
				new MultiLineColDef<Shipment>({
					headerName: this.translationService.getTR('datacenter-shipment.customer-article-reference'),
					field: 'customerArticleReferenceConcat',
					colId: 'customerArticleReference',
					minWidth: 150,
					filter: DatacenterTextFilterComponent,
					filterParams: { filterType: 'SHIPMENT_MULTI_VALUE_TEXT_FILTER' },
					valueType: MultiLineValueTypes.TEXT,
					sortable: true
				})
			],
			[
				'grossWeight',
				new MultiLineColDef<Shipment>({
					headerName: this.translationService.getTR('datacenter-shipment.gross-weight'),
					colId: 'grossWeight',
					field: 'qtyBatchBrutConcat',
					filter: DatacenterNumberFilterComponent,
					filterParams: { filterType: 'SHIPMENT_MULTI_VALUE_NUMBER_FILTER' },
					valueType: MultiLineValueTypes.NUMBER,
					suppressHeaderMenuButton: true,
					sortable: true
				})
			],
			[
				'thickness',
				new MultiLineColDef<Shipment>({
					headerName: this.translationService.getTR('datacenter-shipment.thickness'),
					field: 'thicknessDimensionConcat',
					colId: 'thickness',
					filter: DatacenterTextFilterComponent,
					filterParams: { filterType: 'SHIPMENT_MULTI_VALUE_TEXT_FILTER' },
					valueType: MultiLineValueTypes.NUMBER,
					sortable: true
				})
			],
			[
				'width',
				new MultiLineColDef<Shipment>({
					headerName: this.translationService.getTR('datacenter-shipment.width'),
					field: 'widthDimensionConcat',
					colId: 'width',
					filter: DatacenterTextFilterComponent,
					filterParams: { filterType: 'SHIPMENT_MULTI_VALUE_TEXT_FILTER' },
					valueType: MultiLineValueTypes.NUMBER,
					sortable: true
				})
			],
			[
				'shipTo',
				new MultiLineColDef<Shipment>({
					headerName: this.translationService.getTR('datacenter-shipment.ship-to'),
					field: 'shipToDescription',
					colId: 'shipTo',
					filter: DatacenterTextFilterComponent,
					filterParams: { filterType: 'SHIPMENT_MULTI_VALUE_TEXT_FILTER' },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN,
					autoHeight: true,
					sortable: true,
					valueType: MultiLineValueTypes.TEXT,
					valueGetter: params => {
						const desc = params.data?.shipToDescription;
						if (!desc) {
							return '';
						}
						if (!String(desc).includes(';')) {
							return desc;
						}
						return [...new Set(String(desc).split(';').map(s => s.trim()))].join(';');
					}
				})
			],
			[
				'mill',
				new CommonColDef<Shipment>({
					headerName: this.translationService.getTR('datacenter-shipment.mill'),
					field: 'millDescription',
					colId: 'mill',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN,
					sortable: true,
					autoHeight: true
				})
			],
			[
				'dispatchAdviceNumber',
				new LinkColDef<Shipment>({
					headerName: this.translationService.getTR('datacenter-shipment.dispatch-advice-number'),
					field: 'dispatchAdviceNumber',
					colId: 'dispatchAdviceNumber',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN,
					cellRendererParams: linkToShipmentCardCellRendererParams,
					autoHeight: true
				})
			],
			[
				'dispatchAdviceDate',
				new DateColDef<Shipment>({
					headerName: this.translationService.getTR('datacenter-filter.dispatch-advice-date'),
					colId: 'dispatchAdviceDate',
					field: 'dispatchAdviceDate',
					minWidth: 120,
					maxWidth: 120,
					width: 120,
					cellRendererParams: { inputFormat: 'isoDate', outputFormat: 'date' },
					filter: DatacenterDateFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.DATE_COLUMN
				})
			],
			[
				'loadingProgramNumber',
				new CommonColDef<Shipment>({
					headerName: this.translationService.getTR('datacenter-filter.loading-program-number'),
					colId: 'loadingProgramNumber',
					field: 'loadingProgramNumber',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					autoHeight: true,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'transportMode',
				new CommonColDef<Shipment>({
					headerName: this.translationService.getTR('datacenter-shipment.transport-mode'),
					field: 'transportMode',
					colId: 'transportMode',
					filter: DatacenterDropdownFilterComponent,
					filterParams: { shipmentFields: ['transportMode', 'transportModeDescription'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					autoHeight: true,
					sortable: true,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const batch = params.data;
						return batch.transportModeDescription;
					}
				})
			],
			[
				'transportReference',
				new MultiLineColDef<Shipment>({
					headerName: this.translationService.getTR('datacenter-shipment.transport-reference'),
					colId: 'transportReference',
					field: 'transportReferenceConcat',
					filter: DatacenterTextFilterComponent,
					filterParams: { filterType: 'SHIPMENT_MULTI_VALUE_TEXT_FILTER' },
					valueType: MultiLineValueTypes.TEXT,
					sortable: true
				})
			],
			[
				'carrierNameConcat',
				new MultiLineColDef<Shipment>({
					headerName: this.translationService.getTR('datacenter-filter.carrier-name'),
					colId: 'carrierNameConcat',
					field: 'carrierNameConcat',
					filter: DatacenterDropdownFilterComponent,
					filterParams: {
						filterType: 'SHIPMENT_MULTI_VALUE_DROPDOWN_FILTER',
						batchFields: ['carrierNameConcat', 'carrierNameConcat'],
						removeNoValueFilter: true
					},
					columnType: ColumnType.TRANSLATED_COLUMN,
					sortable: true,
					suppressHeaderMenuButton: false,
					valueType: MultiLineValueTypes.TEXT,
				})
			],
			[
				'soldTo',
				new CommonColDef<Shipment>({
					headerName: this.translationService.getTR('datacenter-filter.sold-to'),
					field: 'soldToDescription',
					colId: 'soldTo',
					filter: DatacenterTextFilterComponent,
					minWidth: 225,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN,
					autoHeight: true
				})
			],
			[
				'customerOrderNumber',
				new LinkMultiValueColDef<Shipment>({
					autoHeight: true,
					headerName: this.translationService.getTR('datacenter-shipment.customer-order-number'),
					colId: 'customerOrderNumber',
					field: 'customerOrderAndItemConcat',
					filter: DatacenterTextFilterComponent,
					filterParams: { filterType: 'SHIPMENT_MULTI_VALUE_TEXT_FILTER' },
					minWidth: 150,
					cellRendererParams: linkToOrderCardCellRendererParams,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN,
					sortable: true
				})
			],
			[
				'supplierOrderNumber',
				new LinkMultiValueColDef<Shipment>({
					autoHeight: true,
					headerName: this.translationService.getTR('datacenter-shipment.supplier-order-number'),
					colId: 'supplierOrderNumber',
					field: 'supplierOrderAndItemConcat',
					minWidth: 150,
					cellRendererParams: linkToOrderCardCellRendererParams,
					filter: DatacenterTextFilterComponent,
					filterParams: { filterType: 'SHIPMENT_MULTI_VALUE_TEXT_FILTER' },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN,
					sortable: true
				})
			],
			[
				'shipmentQuantityNet',
				new NumericColDef<Shipment>({
					headerName: this.translationService.getTR('datacenter-shipment.shipment-quantity-net'),
					colId: 'shipmentQuantityNet',
					field: 'qtyShipmentNet',
					cellRendererParams: { numericFormat: 'weight-with-zero' },
					columnType: ColumnType.NUMBER_COLUMN,
					autoHeight: true
				})
			],
			[
				'shipmentQuantityGross',
				new NumericColDef<Shipment>({
					headerName: this.translationService.getTR('datacenter-shipment.shipment-quantity-gross'),
					colId: 'shipmentQuantityGross',
					field: 'qtyShipmentBrut',
					cellRendererParams: { numericFormat: 'weight-with-zero' },
					columnType: ColumnType.NUMBER_COLUMN,
					autoHeight: true
				})
			],
			[
				'supplierOrderTypeConcat',
				new MultiLineColDef<Shipment>({
					headerName: this.translationService.getTR('datacenter-filter.spot-or-schedule'),
					field: 'supplierOrderTypeConcat',
					colId: 'supplierOrderTypeConcat',
					valueGetter: params => this.getTranslatedSupplierOrderType(params.data),
					filter: DatacenterDropdownFilterComponent,
					filterParams: {
						filterType: 'SHIPMENT_MULTI_VALUE_DROPDOWN_FILTER',
						removeNoValueFilter: true,
					},
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					autoHeight: true,
					valueType: MultiLineValueTypes.TEXT,
					sortable: true,
					multiFilterCodes: new Map<string, string[]>([['spot', ['C']], ['scheduled', ['E', 'F']]]),
					applyLikeCondition: true
				})
			],
			[
				'cdd',
				new MultiLineColDef<Shipment>({
					headerName: this.translationService.getTR('datacenter-filter.cdd'),
					field: 'cddWeek',
					colId: 'cdd',
					filter: DatacenterDateFilterComponent,
					filterParams: { filterType: 'SHIPMENT_MULTI_VALUE_DATE_FILTER' },
					valueType: MultiLineValueTypes.DATE,
					columnType: ColumnType.DATE_COLUMN,
					valueGetter: params => {
						if (!params.data?.cddWeek || params.data.cddWeek.includes('1900-W01')) {
							return '';
						}
						return params.data.cddWeek;
					},
					sortable: true,
					cellRendererParams: { inputFormat: 'isoWeek', outputFormat: 'week' },
				})
			],
			[
				'dimension',
				new MultiLineColDef<Shipment>({
					headerName: this.translationService.getTR('datacenter-filter.dimension'),
					sortable: true,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const thickness = params.data.thicknessDimensionConcat;
						const width = params.data.widthDimensionConcat;
						const length = params.data.lengthDimensionConcat;

						if (
							typeof thickness === 'string' && thickness.includes(';')
							&& typeof width === 'string' && width.includes(';')
						) {
							const lengthArr = typeof length === 'string' ? length.split(';') : [];
							const thicknessArr = thickness.split(';');
							const widthArr = width.split(';');
							const dimArray = [];
							for (const [index] of thicknessArr.entries()) {
								const dim = formatDimension(
									thicknessArr[index],
									widthArr[index],
									lengthArr[index]
								);
								dimArray.push(dim);
							}
							return dimArray.join(';');
						}
						return formatDimension(
							typeof thickness === 'string' ? thickness : '',
							typeof width === 'string' ? width : '',
							typeof length === 'string' ? length : ''
						);
					},
					colId: 'dimension',
					fields: ['thicknessDimensionConcat', 'widthDimensionConcat', 'lengthDimensionConcat'],
					filter: DatacenterDimensionFilterComponent,
					filterParams: { filterType: 'SHIPMENT_DIMENSION_FILTER' },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN,
					valueType: MultiLineValueTypes.DIMENSION,
				})
			],

			[
				'lengthDimensionConcat',
				new MultiLineColDef<Shipment>({
					headerName: this.translationService.getTR('batch-card.length'),
					field: 'lengthDimensionConcat',
					colId: 'lengthDimensionConcat',
					filter: DatacenterTextFilterComponent,
					filterParams: { filterType: 'SHIPMENT_MULTI_VALUE_TEXT_FILTER' },
					sortable: true,
					valueType: MultiLineValueTypes.NUMBER,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const shipment = params.data;
						if (!shipment.lengthDimensionConcat) {
							return '';
						}
						if (shipment.lengthDimensionConcat.includes(';')) {
							const length = shipment.lengthDimensionConcat.split(';');
							return length.map(len => len === '0.000000' ? '' : Number(len)).join(';');
						}
						return shipment.lengthDimensionConcat === '0.000000'
							? '' : String(Number(shipment.lengthDimensionConcat));
					},
				})
			],
			[
				'shipmentReferenceDate',
				new MultiLineColDef<Shipment>({
					headerName: this.translationService.getTR('datacenter-filter.shipment-reference-date'),
					colId: 'shipmentReferenceDate',
					field: 'shipmentReferenceDate',
					suppressHeaderMenuButton: true,
					sortable: true,
					filter: DatacenterTextFilterComponent,
					filterParams: { filterType: 'SHIPMENT_MULTI_VALUE_TEXT_FILTER' },
					valueType: MultiLineValueTypes.TEXT,
				})
			],
			[
				'currentLocationConcat',
				new MultiLineColDef<Shipment>({
					headerName: this.translationService.getTR('datacenter-filter.current-location'),
					field: 'currentLocationDescriptionConcat',
					colId: 'currentLocationConcat',
					filter: DatacenterTextFilterComponent,
					filterParams: {
						filterType: 'SHIPMENT_MULTI_VALUE_TEXT_FILTER',
						shipmentFields: ['currentLocationConcat', 'currentLocationDescriptionConcat']
					},
					suppressHeaderMenuButton: false,
					sortable: true,
					valueType: MultiLineValueTypes.TEXT,
					columnType: ColumnType.TRANSLATED_COLUMN,
				})
			],
			[
				'shipToAddressConcat',
				new MultiLineColDef<Shipment>({
					headerName: this.translationService.getTR('datacenter-filter.ship-to-address'),
					field: 'shipToAddressConcat',
					colId: 'shipToAddressConcat',
					filter: DatacenterTextFilterComponent,
					filterParams: { filterType: 'SHIPMENT_MULTI_VALUE_TEXT_FILTER' },
					valueType: MultiLineValueTypes.TEXT,
					sortable: true,
					suppressHeaderMenuButton: false,
					autoHeight: true
				})
			],
			[
				'deliveryModeConcat',
				new MultiLineColDef<Shipment>({
					headerName: this.translationService.getTR('datacenter-filter.delivery-mode'),
					field: 'deliveryModeDescriptionConcat',
					colId: 'deliveryModeConcat',
					filter: DatacenterDropdownFilterComponent,
					filterParams: {
						filterType: 'DROPDOWN_FILTER',
						orderFields: ['deliveryMode', 'deliveryModeDescription']
					},
					suppressHeaderMenuButton: false,
					sortable: true,
					valueType: MultiLineValueTypes.TEXT,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const shipment = params.data;
						return shipment.deliveryModeDescriptionConcat;
					}
				})
			],
			[
				'status',
				new MultiLineColDef<Shipment>({
					headerName: this.translationService.getTR('datacenter-filter.batch-status'),
					field: 'status',
					colId: 'status',
					filter: DatacenterDropdownFilterComponent,
					filterParams: {
						filterType: 'SHIPMENT_MULTI_VALUE_DROPDOWN_FILTER',
						batchFields: ['status', 'statusDescription'],
					},
					sortable: true,
					valueType: MultiLineValueTypes.TEXT,
					columnType: ColumnType.TRANSLATED_COLUMN,
					suppressHeaderMenuButton: false,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const shipment = params.data;
						return shipment.statusDescription;
					}
				})
			],
			[
				'endUser',
				new MultiLineColDef<Shipment>({
					headerName: this.translationService.getTR('datacenter-filter.end-user'),
					field: 'endUserDescription',
					colId: 'endUser',
					filter: DatacenterTextFilterComponent,
					filterParams: {
						filterType: 'SHIPMENT_MULTI_VALUE_TEXT_FILTER',
						shipmentFields: ['endUser', 'endUserDescription']
					},
					valueType: MultiLineValueTypes.TEXT,
					suppressHeaderMenuButton: false,
					sortable: true,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const shipment = params.data;
						return shipment.endUserDescription;
					}
				})
			],
			[
				'heatNumberConcat',
				new MultiLineColDef<Shipment>({
					headerName: this.translationService.getTR('datacenter-filter.heat-number'),
					field: 'heatNumberConcat',
					colId: 'heatNumberConcat',
					filter: DatacenterTextFilterComponent,
					filterParams: { filterType: 'SHIPMENT_MULTI_VALUE_TEXT_FILTER' },
					suppressHeaderMenuButton: false,
					sortable: true,
					valueType: MultiLineValueTypes.TEXT,
				})
			],
			[
				'millOrderNumberConcat',
				new MultiLineColDef<Shipment>({
					headerName: this.translationService.getTR('batch-card.mill-order-number'),
					field: 'millOrderNumberConcat',
					colId: 'millOrderNumberConcat',
					filter: DatacenterTextFilterComponent,
					filterParams: { filterType: 'SHIPMENT_MULTI_VALUE_TEXT_FILTER' },
					suppressHeaderMenuButton: false,
					valueType: MultiLineValueTypes.TEXT,
					sortable: true
				})
			],
			[
				'packageSerialNumberConcat',
				new MultiLineColDef<Shipment>({
					headerName: this.translationService.getTR('batch-card.package-serial-number'),
					field: 'packageSerialNumberConcat',
					colId: 'packageSerialNumberConcat',
					filter: DatacenterTextFilterComponent,
					filterParams: { filterType: 'SHIPMENT_MULTI_VALUE_TEXT_FILTER' },
					suppressHeaderMenuButton: false,
					valueType: MultiLineValueTypes.TEXT,
					sortable: true
				})
			],
			[
				'destinationByBoatConcat',
				new CommonColDef<Shipment>({
					headerName: this.translationService.getTR('order.place.delivery'),
					field: 'destinationByBoatConcat',
					colId: 'destinationByBoatConcat',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN,
					sortable: true
				})
			],
			[
				'productCategory',
				new MultiLineColDef<Shipment>({
					headerName: this.translationService.getTR('datacenter-filter.product-category'),
					field: 'productCategoryDescription',
					colId: 'productCategory',
					filter: DatacenterDropdownFilterComponent,
					filterParams: {
						orderFields: [
							'productCategory',
							'productCategoryDescription'
						]
					},
					suppressHeaderMenuButton: false,
					sortable: true,
					valueType: MultiLineValueTypes.TEXT,
					columnType: ColumnType.TRANSLATED_COLUMN,
				})
			],
			[
				'serviceLevelConcat',
				new MultiLineColDef<Shipment>({
					headerName: this.translationService.getTR('datacenter-filter.service-level'),
					field: 'serviceLevelConcat',
					colId: 'serviceLevelConcat',
					filter: DatacenterDropdownFilterComponent,
					filterParams: {
						filterType: 'DROPDOWN_FILTER',
						orderFields: [
							'serviceLevel',
							'serviceLevelDescription'
						]
					},
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					sortable: true,
					valueType: MultiLineValueTypes.TEXT,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const shipment = params.data;
						return shipment.serviceLevelDescriptionConcat;
					}
				})
			],
			[
				'supplierInvoiceNumberConcat',
				new LinkMultiValueColDef<Shipment>({
					autoHeight: true,
					headerName: this.translationService.getTR('batch.supplier.invoice.number'),
					colId: 'supplierInvoiceNumberConcat',
					field: 'supplierInvoiceNumberConcat',
					cellRendererParams: linkToShipmentCardCellRendererParams,
					filter: DatacenterTextFilterComponent,
					filterParams: { filterType: 'SHIPMENT_MULTI_VALUE_TEXT_FILTER' },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN,
					sortable: true
				})
			],
			[
				'htsCodeConcat',
				new MultiLineColDef<Shipment>({
					headerName: this.translationService.getTR('datacenter-filter.hts-code'),
					colId: 'htsCodeConcat',
					field: 'htsCodeConcat',
					filter: DatacenterTextFilterComponent,
					filterParams: { filterType: 'SHIPMENT_MULTI_VALUE_TEXT_FILTER' },
					valueType: MultiLineValueTypes.TEXT,
					suppressHeaderMenuButton: false,
					sortable: true,
				})
			],
			[
				'producedLengthDimensionConcat',
				new MultiLineColDef<Shipment>({
					headerName: this.translationService.getTR('datacenter-filter.producedLength'),
					colId: 'producedLengthDimensionConcat',
					field: 'producedLengthDimensionConcat',
					filter: DatacenterTextFilterComponent,
					filterParams: { filterType: 'SHIPMENT_MULTI_VALUE_TEXT_FILTER' },
					valueType: MultiLineValueTypes.NUMBER,
					sortable: true,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const shipment = params.data;
						if (!shipment.producedLengthDimensionConcat
							|| !shipment.form.startsWith('04')) {
							return '';
						}
						if (shipment.producedLengthDimensionConcat.includes(';')) {
							const length = shipment.producedLengthDimensionConcat.split(';');
							return length.map(len => len === '0.000000' ? '' : Number(len) / 1000).join(';');
						}
						return shipment.producedLengthDimensionConcat === '0.000000'
							? '' : String(Number(shipment.producedLengthDimensionConcat) / 1000);
					},
				})
			],
			[
				'salesQuarterConcat',
				new MultiLineColDef<Shipment>({
					headerName: this.translationService.getTR('datacenter-filter.sales-quarter'),
					colId: 'salesQuarterConcat',
					field: 'salesQuarterConcat',
					filter: DatacenterTextFilterComponent,
					filterParams: { filterType: 'SHIPMENT_MULTI_VALUE_TEXT_FILTER' },
					suppressHeaderMenuButton: false,
					valueType: MultiLineValueTypes.TEXT,
					sortable: true
				})
			],
			[
				'choice',
				new MultiLineColDef<Shipment>({
					headerName: this.translationService.getTR('datacenter-filter.choice'),
					field: 'choiceDescription',
					colId: 'choice',
					filter: DatacenterDropdownFilterComponent,
					filterParams: {
						filterType: 'SHIPMENT_MULTI_VALUE_DROPDOWN_FILTER',
						orderFields: ['choice', 'choiceDescription'],
					},
					suppressHeaderMenuButton: false,
					sortable: true,
					valueType: MultiLineValueTypes.TEXT,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const shipment = params.data;
						return shipment.choiceDescription;
					}
				})
			],
			[
				'productDescriptionStu',
				new MultiLineColDef<Shipment>({
					headerName: this.translationService.getTR('datacenter-filter.product-description'),
					colId: 'productDescriptionStu',
					field: 'productDescriptionStu',
					filter: DatacenterTextFilterComponent,
					filterParams: { filterType: 'SHIPMENT_MULTI_VALUE_TEXT_FILTER' },
					valueType: MultiLineValueTypes.TEXT,
					suppressHeaderMenuButton: false,
					sortable: true,
				})
			],
			[
				'readyToShipDateConcat',
				new MultiLineColDef<Shipment>({
					headerName: this.translationService.getTR('datacenter-filter.ready-to-ship-date'),
					colId: 'readyToShipDateConcat',
					field: 'readyToShipDateConcat',
					cellRendererParams: { inputFormat: 'isoDate', outputFormat: 'date' },
					filter: DatacenterDateFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.DATE_COLUMN,
					sortable: true,
					valueType: MultiLineValueTypes.DATE
				})
			],
			[
				'stockPartner',
				new MultiLineColDef<Shipment>({
					headerName: this.translationService.getTR('schedule-business-order.stock-partner'),
					field: 'stockPartnerDescription',
					colId: 'stockPartner',
					filter: DatacenterTextFilterComponent,
					filterParams: { filterType: 'SHIPMENT_MULTI_VALUE_TEXT_FILTER' },
					suppressHeaderMenuButton: false,
					valueType: MultiLineValueTypes.TEXT,
					sortable: true,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const shipment = params.data;
						return shipment.stockPartnerDescription;
					}
				})
			],
			[
				'form',
				new MultiLineColDef<Shipment>({
					headerName: this.translationService.getTR('datacenter-filter.form'),
					field: 'formDescription',
					colId: 'form',
					filter: DatacenterDropdownFilterComponent,
					filterParams: {
						filterType: 'DROPDOWN_FILTER',
						orderFields: ['form', 'formDescription'],
					},
					suppressHeaderMenuButton: false,
					sortable: true,
					valueType: MultiLineValueTypes.TEXT,
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const shipment = params.data;
						return shipment.formDescription;
					}
				})
			],
		]);
	}

	private getTranslatedSupplierOrderType(shipment?: Shipment) {
		if (!shipment) {
			return '';
		}
		const orderType = shipment.supplierOrderTypeConcat;
		if (typeof orderType === 'string' && orderType.includes(';')) {
			const uniqueValues = orderType.split(';');
			return uniqueValues.map(value => {
				if (value === 'C') {
					return this.translationService.getTR('filter.spot-order');
				}
				if (value === 'E' || value === 'F') {
					return this.translationService.getTR('filter.schedule-business-order');
				}
				return value;
			}).join(';');
		}
		if (orderType === 'C') {
			return this.translationService.getTR('filter.spot-order');
		}
		if (orderType === 'E' || orderType === 'F') {
			return this.translationService.getTR('filter.schedule-business-order');
		}
		return typeof orderType === 'string' ? orderType : '';
	}
}

export const DEFAULT_VIEW_SHIPMENT_DATACENTER: UserDatacenterView = {
	columnState: [
		{
			colId: 'dispatchAdviceNumber',
			pinned: 'left'
		},
		{
			colId: 'transportMode',
			pinned: 'left'
		},
		{
			colId: 'transportReference',
			pinned: 'left'
		},
		{
			colId: 'shipTo',
			pinned: 'left'
		},
		{
			colId: 'batchReference'
		},
		{
			colId: 'mill'
		},
		{
			colId: 'steelgrade'
		},
		{
			colId: 'grossWeight'
		},
		{
			colId: 'thickness'
		},
		{
			colId: 'width'
		},
		{
			colId: 'customerOrderNumber'
		},
		{
			colId: 'supplierOrderNumber'
		},
		{
			colId: 'shipmentQuantityNet'
		},
		{
			colId: 'dispatchAdviceDate'
		},
	],
	sortModel: [
		{
			colId: 'dispatchAdviceDate',
			sort: 'desc'
		},
		{
			colId: 'dispatchAdviceNumber',
			sort: 'desc'
		}
	],
	filterState: [],
	type: 'shipment',
	isDefault: true,
	name: 'Default',
	id: -99
};
